import React, { useState, useEffect } from 'react';
import { Box, keyframes } from '@mui/system';
import { styled } from '@mui/material/styles';

// Keyframes for the pipe rotation - smoother easing
const rotateKeyframe = keyframes`
  0% {
    transform: rotateY(0deg) rotateX(15deg);
  }
  100% {
    transform: rotateY(360deg) rotateX(15deg);
  }
`;

// Keyframes for the pipe pulsing
const pulseKeyframe = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
`;

// Shine effect for the pipe
const shineKeyframe = keyframes`
  0% {
    background-position: -100px -100px;
  }
  75% {
    background-position: 200px 100px;
  }
  100% {
    background-position: 200px 100px;
  }
`;

// Styled pipe container with improved 3D perspective
const PipeContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '160px',
  height: '160px',
  margin: '0 auto',
  perspective: '1000px',
  transformStyle: 'preserve-3d',
  animation: `${pulseKeyframe} 4s infinite ease-in-out`,
}));

// Main pipe with smoother rotation
const Pipe = styled(Box)(({ theme, isActive }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  animation: `${rotateKeyframe} ${isActive ? 5 : 8}s infinite ${isActive ? 'linear' : 'ease-in-out'}`,
  transformStyle: 'preserve-3d',
  transform: 'rotateX(15deg)',
  zIndex: 10,
}));

// Improved pipe segments with better 3D and shading
const PipeSegment = styled(Box)(({ theme, color, rotate, translateZ, index }) => ({
  position: 'absolute',
  width: '35px',
  height: '70px',
  left: 'calc(50% - 17.5px)',
  top: 'calc(50% - 35px)',
  borderRadius: '8px',
  backgroundColor: color || theme.palette.primary.main,
  transform: `rotateY(${rotate || 0}deg) translateZ(${translateZ || 30}px)`,
  boxShadow: `
    inset 0 0 15px rgba(255, 255, 255, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.15)
  `,
  background: `linear-gradient(135deg, ${color} 0%, ${color}DD 50%, ${color}AA 100%)`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '8px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 100%)',
    animation: `${shineKeyframe} 4s infinite linear`,
    animationDelay: `${index * 0.5}s`,
    backgroundSize: '200px 100px',
    backgroundRepeat: 'no-repeat',
  }
}));

const PipeAnimation = () => {
  const [isActive, setIsActive] = useState(false);
  
  // Toggle active state every few seconds to simulate pipe activity
  useEffect(() => {
    const interval = setInterval(() => {
      setIsActive(prev => !prev);
    }, 7000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '220px', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      overflow: 'hidden',
      position: 'relative',
      mb: 4
    }}>
      <PipeContainer>
        <Pipe isActive={isActive}>
          {/* Create more pipe segments for better 3D effect */}
          <PipeSegment color="#2979FF" rotate={0} translateZ={35} index={0} />
          <PipeSegment color="#2962FF" rotate={45} translateZ={35} index={1} />
          <PipeSegment color="#2962FF" rotate={90} translateZ={35} index={2} />
          <PipeSegment color="#1E88E5" rotate={135} translateZ={35} index={3} />
          <PipeSegment color="#1976D2" rotate={180} translateZ={35} index={4} />
          <PipeSegment color="#1565C0" rotate={225} translateZ={35} index={5} />
          <PipeSegment color="#0D47A1" rotate={270} translateZ={35} index={6} />
          <PipeSegment color="#0D47A1" rotate={315} translateZ={35} index={7} />
        </Pipe>
      </PipeContainer>
    </Box>
  );
};

export default PipeAnimation; 